import styled, { css, keyframes } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export interface FormControlStateProps {
  $invalid?: boolean;
  $disabled?: boolean;
  $focusVisible?: boolean;
}

export const FormField = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.sm};
  font-family: ${vars.typography.family.sans};
`;

export const FieldLabel = styled.label<{
  $invalid?: boolean;
  $active?: boolean;
}>`
  display: flex;
  min-height: 20px;
  align-items: center;
  gap: ${vars.spacing.xs};
  color: ${({ $invalid }) =>
    $invalid ? vars.color.control.borderError : vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.sm};
`;

export const RequiredIndicator = styled.span`
  color: ${vars.color.control.required};
`;

export const FormControl = styled.div<FormControlStateProps>`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${vars.spacing.sm};
  border: ${({ $invalid }) =>
    `${$invalid ? vars.borderWidth.medium : vars.borderWidth.thin} solid ${
      $invalid ? vars.color.control.borderError : vars.color.control.border
    }`};
  border-radius: ${vars.radius.sm};
  background: ${({ $disabled }) =>
    $disabled
      ? vars.color.control.backgroundDisabled
      : vars.color.control.background};

  &:focus-within {
    border-width: ${vars.borderWidth.medium};
    border-color: ${({ $invalid }) =>
      $invalid ? vars.color.control.borderError : vars.color.border.accent};
  }

  &:focus-within ${FieldLabel} {
    color: ${({ $invalid }) =>
      $invalid ? vars.color.control.borderError : vars.color.text.primary};
  }

  ${({ $focusVisible }) =>
    $focusVisible &&
    css`
      outline: none;
      box-shadow: ${vars.shadow.focus};
    `}
`;

export const FormControlContent = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 56px;
  flex: 1 0 0;
  flex-direction: column;
  align-items: flex-start;
  background: ${({ $disabled }) =>
    $disabled
      ? vars.color.control.backgroundDisabled
      : vars.color.control.background};
`;

const controlTypography = css`
  color: ${vars.color.control.text};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};

  &::placeholder {
    color: ${vars.color.control.placeholder};
  }
`;

export const FormValue = styled.span`
  max-width: 100%;
  color: ${vars.color.control.text};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
`;

export const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 32px;
  border: 0;
  outline: 0;
  background: var(--inzeloop-ds-color-background-transparent);
  ${controlTypography}

  &:disabled {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
    -webkit-text-fill-color: ${vars.color.text.disabled};
  }
`;

export const Textarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: var(--inzeloop-ds-color-background-transparent);
  resize: none;
  ${controlTypography}

  &:disabled {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
    -webkit-text-fill-color: ${vars.color.text.disabled};
  }
`;

export const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 40px;
  border: ${vars.borderWidth.thin} solid ${vars.color.control.border};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.control.background};
  padding: ${vars.spacing.sm} ${vars.spacing.md};
  ${controlTypography}

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  &:disabled {
    background: ${vars.color.control.backgroundDisabled};
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }
`;

export const Checkbox = styled.input.attrs({ type: "checkbox" })`
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  cursor: default;

  &:not(:checked) {
    accent-color: ${vars.color.action.primary};
  }

  &:checked,
  &:indeterminate {
    accent-color: ${vars.color.action.primaryPressed};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const FieldError = styled.div.attrs({ role: "alert" })`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.xs};
  color: ${vars.color.control.errorText};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
`;

export const FieldHint = styled.div`
  color: ${vars.color.text.muted};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  line-height: ${vars.typography.lineHeight.xs};
`;

export const SelectRoot = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 160px;
  min-height: 65px;
  flex-shrink: 0;
  flex-direction: column;
`;

export const SelectTrigger = styled(ButtonBase).attrs({ type: "button" })<{
  $open?: boolean;
  $keyboardFocus?: boolean;
  $invalid?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 65px;
  flex-direction: column;
  align-items: flex-start;
  border: ${vars.borderWidth.thin} solid ${vars.color.control.border};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.control.background};
  padding-top: ${vars.spacing.xxs};
  padding-right: ${vars.spacing.md};
  padding-left: ${vars.spacing.md};
  color: inherit;
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
  text-align: left;

  &:focus {
    outline: none;
  }

  ${({ $keyboardFocus }) =>
    $keyboardFocus &&
    css`
      box-shadow: ${vars.shadow.focus};
    `}

  &:disabled {
    background: ${vars.color.action.secondaryHover};
    cursor: not-allowed;
  }

  ${({ $open }) =>
    $open &&
    css`
      border: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
      border-bottom: 0;
      border-radius: ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none}
        ${vars.radius.none};
      background: ${vars.color.control.background};
    `}

  ${({ $invalid }) =>
    $invalid &&
    css`
      border: ${vars.borderWidth.medium} solid ${vars.color.control.borderError};
    `}
`;

export const SelectContentRow = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${vars.spacing.sm};
`;

export const SelectIconLabel = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: ${vars.spacing.sm};
  overflow: hidden;
`;

const cursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const SelectValue = styled.span<{ $showCursor?: boolean }>`
  display: block;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: ${vars.color.control.text};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ $showCursor }) =>
    $showCursor &&
    css`
      &::after {
        content: "|";
        margin-left: var(--inzeloop-ds-spacing-1px);
        animation: ${cursorBlink} 1s step-start infinite;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

export const SelectIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`;

export const SelectMenu = styled.div<{ $width?: string }>`
  position: absolute;
  top: 102%;
  right: 0;
  z-index: ${vars.zIndex.dropdown};
  box-sizing: border-box;
  display: flex;
  width: ${({ $width }) => $width || "100%"};
  max-height: 400px;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  border-right: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-bottom: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-left: ${vars.borderWidth.medium} solid ${vars.color.border.accent};
  border-radius: ${vars.radius.none} ${vars.radius.none} ${vars.radius.sm}
    ${vars.radius.sm};
  background: ${vars.color.control.background};
  padding-left: ${vars.spacing.sm};
  color: ${vars.color.control.text};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
  box-shadow: ${vars.shadow.dropdown};
`;

export const SelectOption = styled.div<{ $disabled?: boolean }>`
  display: flex;
  min-height: 32px;
  flex-shrink: 0;
  align-items: center;
  gap: ${vars.spacing.sm};
  border-radius: ${vars.radius.xs};
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "default")};

  input {
    pointer-events: none;
  }

  &:hover {
    background: ${({ $disabled }) =>
      $disabled
        ? vars.color.background.transparent
        : vars.color.action.secondaryHover};
  }
`;

export const WorkspaceFieldLabel = styled(FieldLabel)`
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.xs};
`;

export const WorkspaceFormControl = styled(FormControl)`
  min-height: ${vars.spacing.px56};
  justify-content: center;
  gap: ${vars.spacing.xxs};
  padding: ${vars.spacing.px7} ${vars.spacing.md};
  border-color: ${({ $invalid, $disabled }) =>
    $invalid
      ? vars.color.control.borderError
      : $disabled
        ? vars.color.border.inputStrong
        : vars.color.border.input};

  ${FieldLabel} {
    min-height: 0;
    color: ${({ $invalid, $disabled }) =>
      $invalid
        ? vars.color.control.borderError
        : $disabled
          ? vars.color.text.disabled
          : vars.color.text.primary};
    font-size: ${vars.typography.size.xs};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.xs};
  }
`;

export const WorkspaceInput = styled(Input)`
  padding: 0;
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};

  &:disabled {
    color: ${vars.color.text.primary};
    -webkit-text-fill-color: ${vars.color.text.primary};
  }
`;

export const WorkspaceSelectField = styled.div`
  width: 100%;

  ${FieldLabel} {
    font-size: ${vars.typography.size.xs};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.xs};
  }

  ${SelectValue}, ${SelectMenu} {
    font-size: ${vars.typography.size.sm};
    font-weight: ${vars.typography.weight.regular};
    line-height: ${vars.typography.lineHeight.sm};
  }

  ${SelectTrigger}[aria-expanded="false"]:not([aria-describedby]) {
    border-color: ${vars.color.border.input};
  }
`;
